/**
 * Branded identifier and date primitives shared by every layer.
 *
 * Contracts are types plus inert constants only (ADR-0001). Constructing and
 * validating branded values at runtime is the domain layer's job; these brands
 * exist so illegal mixing (a HabitId where an ObjectiveId belongs, a raw string
 * where a date belongs) fails to compile.
 *
 * ID scheme: ADR-0003. Date semantics: ADR-0006.
 */

declare const brandSymbol: unique symbol;

export type Brand<TBase, TBrand extends string> = TBase & {
  readonly [brandSymbol]: TBrand;
};

/**
 * UUIDv7 in canonical lowercase hyphenated form (RFC 9562).
 * Lexicographic order of the string IS the canonical order of the event log.
 * Generators must be strictly monotonic per device (ADR-0003).
 */
export type EventId = Brand<string, 'EventId'>;

export type HabitId = Brand<string, 'HabitId'>;
export type ObjectiveId = Brand<string, 'ObjectiveId'>;
/**
 * The checkable primitive inside an objective's action plan (ADR-0002
 * addendum 2026-07-02). Superset of what was, in Wave 0, called a milestone:
 * a milestone is now an optional grouping label carried BY an action
 * (`ActionCreatedEvent.milestoneTag`), not a separate entity with its own ID.
 */
export type ActionId = Brand<string, 'ActionId'>;
export type BadgeId = Brand<string, 'BadgeId'>;

/**
 * A brain-dump note (R8.1, Phase 6, ADR-0002 addendum 2026-07-07). Capture-
 * only; never checkable, never dated, never objective-linked while it remains
 * a note (see `NoteCreatedEvent`).
 */
export type NoteId = Brand<string, 'NoteId'>;

/**
 * A Priority (R8.2, Phase 6, ADR-0002 addendum 2026-07-07) — the one
 * free-standing checkable entity Compass allows outside an objective's action
 * plan, hard-capped at 5 per calendar day (Non-Goal #1's narrow carve-out,
 * PRD §3).
 */
export type PriorityId = Brand<string, 'PriorityId'>;

/**
 * An Idea (R8.4, Phase 6, ADR-0002 addendum 2026-07-07). Category-tagged
 * capture, promotable only to an objective action (never to a Priority,
 * unlike a brain-dump note).
 */
export type IdeaId = Brand<string, 'IdeaId'>;

/**
 * Calendar date in the recording device's local time, 'YYYY-MM-DD'.
 * Compares correctly as a string. The unit of all day/week math (ADR-0006).
 */
export type IsoDate = Brand<string, 'IsoDate'>;

/** Instant in UTC, ISO 8601 with trailing 'Z'. Audit only — never day math. */
export type IsoDateTime = Brand<string, 'IsoDateTime'>;

/**
 * The IsoDate of a Monday, identifying a Mon-Sun week (ADR-0006).
 * Branded separately so arbitrary dates cannot be passed as week keys.
 */
export type WeekStart = Brand<string, 'WeekStart'>;

/**
 * Calendar month in the recording device's local time, 'YYYY-MM' (ADR-0011).
 * The bucketing key for the completion-history view. Compares correctly as a
 * string, same convention as `IsoDate`. Derived from `IsoDate` by truncation
 * (`isoDate.slice(0, 7)`) -- never stored or computed independently, so a
 * `YearMonth` and the `IsoDate`s it summarizes can never disagree.
 */
export type YearMonth = Brand<string, 'YearMonth'>;

/**
 * Server-assigned position in the canonical log (D1 server_seq, ADR-0004/0005).
 * Opaque to the client: store and echo. 0 means "from the beginning".
 * Transport-only — never used for domain ordering.
 */
export type SyncCursor = Brand<number, 'SyncCursor'>;

/** The cursor value that pulls the full log (fresh device restore). */
export const INITIAL_SYNC_CURSOR = 0 as SyncCursor;
